/**
 * UplinkAdaptation (F5, flag av_uplink_adapt) — publisher only.
 * v2 (2026-10-19, after Production degradation report): the first version
 * judged loss on 3-s windows fed by RTCP receiver reports that are not aligned
 * to those windows, so two spiky windows (6 s) were enough to drop the host mic
 * to 24 kbps — and with RED on, that is ~12 kbps of real voice. It also never
 * restored the cap when the flag was switched off. Now: rolling 30-s loss,
 * step down only on >8 % sustained, floor 32 kbps, step back after 30 s clean,
 * and ALWAYS restore 48 kbps on disable/unmount.
 * Also re-publishes the mic after a LiveKit reconnect if it came back unpublished.
 */
import { useEffect } from "react";
import { useRoomContext } from "@livekit/components-react";
import { RoomEvent, Track } from "livekit-client";

const STEPS = [48000, 32000];
const TICK_MS = 5000;
const WINDOWS = 6;        // 6 × 5 s = 30 s rolling
const DOWN_PCT = 8;
const UP_PCT = 2;

export default function UplinkAdaptation({ enabled = false }) {
  const room = useRoomContext();

  useEffect(() => {
    if (!room || !enabled) return undefined;
    const lp = room.localParticipant;
    let step = 0, lastLost = null, lastSent = null, alive = true, cleanSince = 0;
    const hist = [];

    const sender = () => lp.getTrackPublication(Track.Source.Microphone)?.track?.sender;
    const apply = async (idx) => {
      const s = sender(); if (!s) return;
      try {
        const params = s.getParameters(); if (!params.encodings?.length) params.encodings = [{}];
        params.encodings[0].maxBitrate = STEPS[idx];
        await s.setParameters(params); step = idx;
        window.dispatchEvent(new CustomEvent("pandax:uplink-adapt", { detail: { bitrate: STEPS[idx] } }));
      } catch { /* unsupported */ }
    };
    const tick = async () => {
      if (!alive || room.state !== "connected") return;
      const s = sender(); if (!s) return;
      let lost = 0, sent = 0;
      try { (await s.getStats()).forEach((r) => { if (r.type === "remote-inbound-rtp") lost = r.packetsLost || 0; if (r.type === "outbound-rtp") sent = r.packetsSent || 0; }); } catch { return; }
      if (lastLost === null) { lastLost = lost; lastSent = sent; return; }  // seed only — no cumulative first window
      const dl = Math.max(0, lost - lastLost), ds = Math.max(0, sent - lastSent); lastLost = lost; lastSent = sent;
      if (ds <= 0) return;
      hist.push([ds, dl]); if (hist.length > WINDOWS) hist.shift();
      if (hist.length < WINDOWS) return;
      const S = hist.reduce((a, x) => a + x[0], 0), L = hist.reduce((a, x) => a + x[1], 0);
      const pct = (L / (S + L)) * 100;
      if (pct > DOWN_PCT && step < STEPS.length - 1) { hist.length = 0; cleanSince = 0; apply(step + 1); }
      else if (pct < UP_PCT && step > 0) { cleanSince += 1; if (cleanSince >= WINDOWS) { cleanSince = 0; hist.length = 0; apply(step - 1); } }
      else cleanSince = 0;
    };
    const onReconnected = async () => {
      await new Promise((r) => setTimeout(r, 1500));
      const pub = lp.getTrackPublication(Track.Source.Microphone);
      if (!pub?.track && lp.permissions?.canPublish) { try { await lp.setMicrophoneEnabled(true); } catch { /* user can retry */ } }
      if (step > 0) apply(step);
    };
    room.on(RoomEvent.Reconnected, onReconnected);
    const t = setInterval(tick, TICK_MS);
    return () => {
      alive = false; clearInterval(t); room.off(RoomEvent.Reconnected, onReconnected);
      if (step > 0) apply(0);  // flag off / leaving: never leave the mic capped
    };
  }, [room, enabled]);

  return null;
}
